/**
 * GridCrash — Modal System
 *
 * Loop 12 additions:
 * - showMascotPicker() — opens Scores modal on the emoji-picker tab
 * - Share Score button on game-end card (Web Share API / clipboard fallback)
 *
 * Loop 4 changes:
 * - Leaderboard gains tier tabs (Easy / Medium / Expert)
 * - Game-end card shows full score breakdown
 * - Help modal has improved visual hierarchy
 * - Focus trap: Tab key cycles within open modal
 */

import type { DifficultyConfig, GameState } from "../game/engine.js";
import { DIFFICULTIES } from "../game/engine.js";
import {
  getTopScores,
  addScore,
  formatRelativeTime,
  rankLabel,
  formatTime,
  type LeaderboardEntry,
} from "../game/leaderboard.js";
import { buildEmojiPickerHTML } from "../main.js";
import { ACHIEVEMENTS, getAchievementState, type Achievement } from "../game/achievements.js";
import {
  fetchTopScores,
  submitScore,
  getLastUsedName,
  setLastUsedName,
  validateName,
  isGlobalLeaderboardEnabled,
  type GlobalScore,
  type TimeRange,
} from "../game/global_leaderboard.js";
import {
  getDailyProgress,
  getDailyTierStatus,
  getDailySummary,
  submitDailyGlobalScore,
  type DailyTier,
} from "../game/daily.js";
import { buildEmojiGrid, buildShareText } from "../game/share_grid.js";
import {
  getAvailableTitles,
  getEquippedTitle,
  setEquippedTitle,
  getShowcase,
  toggleShowcase,
} from "../game/titles.js";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ModalCallbacks {
  onNewGame: () => void;
  onChangeDifficulty: (d: DifficultyConfig) => void;
  onStartDailyTier: (tier: DailyTier) => void;
  /** Opening reveal + fatal mine coords for the share emoji grid. */
  getShareCells?: () => {
    start: [number, number] | null;
    fatal: [number, number] | null;
  };
  /** Fired after a theme/animation equip so the board + mascot refresh. */
  onCosmeticsChanged?: () => void;
  /** Fired after a quest claim so the HUD chip refreshes. */
  onQuestsChanged?: () => void;
  /** Starts a campaign level from the map (6B). */
  onStartCampaignLevel?: (levelNum: number) => void;
  /** Starts a solo game racing a Top Runs ghost (Phase 7). */
  onStartGhostRace?: (
    meta: import("../game/replay.js").ReplayMeta,
    events: import("../game/replay.js").ReplayEvent[]
  ) => void;
}

// ─── Modal Manager ────────────────────────────────────────────────────────────

export class ModalManager {
  private overlay!: HTMLElement;
  private panel!: HTMLElement;
  private callbacks: ModalCallbacks;
  private activeResizeObserver: ResizeObserver | null = null;

  constructor(rootEl: HTMLElement, callbacks: ModalCallbacks) {
    this.callbacks = callbacks;
    this.buildOverlay(rootEl);
  }

  // ─── Public API ─────────────────────────────────────────────────────────────

  showHelp(): void {
    this.panel.className = "modal-panel";
    this.renderHelp();
    this.open();
  }

  showLeaderboard(tierKey: string, tierLabel: string): void {
    this.panel.className = "modal-panel";
    this.renderLeaderboard(tierKey, tierLabel, false);
    this.open();
  }

  /**
   * Opens the Scores modal with the emoji-picker tab active.
   * Called when the user clicks the mascot button in the header.
   */
  showMascotPicker(
    tierKey: string,
    tierLabel: string,
    onSelect: (emoji: string) => void
  ): void {
    this.panel.className = "modal-panel";
    this.renderLeaderboard(tierKey, tierLabel, true, onSelect);
    this.open();
  }

  /** Opens the Scores modal with the Quests tab active (HUD chip target). */
  showQuests(tierKey: string, tierLabel: string): void {
    this.panel.className = "modal-panel";
    this.renderLeaderboard(tierKey, tierLabel, false, undefined, true);
    this.open();
  }

  /** Opens the campaign level map (6B). */
  async showCampaign(): Promise<void> {
    this.panel.className = "modal-panel";
    this.panel.innerHTML = "";
    this.panel.setAttribute("aria-labelledby", "modal-title");
    this.panel.appendChild(this.buildHeader("⛰️ The Climb"));

    const body = document.createElement("div");
    body.className = "modal-body";
    this.panel.appendChild(body);

    const { renderCampaignMap } = await import("./campaign_tab.js");
    renderCampaignMap(body, {
      onPlayLevel: (num) => {
        this.close();
        this.callbacks.onStartCampaignLevel?.(num);
      },
    });
    this.open();
  }

  /** Opens the daily challenge info + tier picker modal. */
  showDailyChallenge(): void {
    this.panel.className = "modal-panel";
    this.renderDailyChallenge();
    this.open();
  }

  /**
   * Opens the daily-complete modal shown right after the 3rd tier finishes.
   * Surfaces the combined score and the global submit prompt immediately, so the
   * player doesn't have to reopen the Daily Challenge screen to post their score.
   */
  showDailyComplete(): void {
    this.panel.className = "modal-panel";
    this.renderDailyComplete();
    this.open();
  }

  showGameEnd(
    state: Readonly<GameState>,
    mode: "random" | "deterministic"
  ): void {
    const won = state.phase === "WIN";
    const { score, timeElapsed, difficulty, grid } = state;

    let correctFlags = 0;
    let wrongFlags   = 0;
    for (const row of grid) {
      for (const cell of row) {
        if (!cell.isFlagged) continue;
        if (cell.isMine) correctFlags++;
        else wrongFlags++;
      }
    }

    const { entries, rank } = addScore({
      score,
      tier: difficulty.leaderboardKey,
      mode,
    });

    // ── Share payload: header + Wordle-style emoji grid ──
    const shareCells = this.callbacks.getShareCells?.() ?? { start: null, fatal: null };
    const emojiGrid = buildEmojiGrid({
      rows: difficulty.rows,
      cols: difficulty.cols,
      grid,
      startCell: shareCells.start,
      fatalCell: won ? null : shareCells.fatal,
    });
    const shareText = buildShareText({
      won,
      score,
      diffLabel: difficulty.label,
      timeLabel: formatTime(timeElapsed),
      correctFlags,
      mineCount: difficulty.mineCount,
      emojiGrid,
    });

    this.renderGameEnd(
      won, score, timeElapsed, difficulty,
      rank, entries, mode,
      correctFlags, wrongFlags,
      shareText
    );
    this.open();
  }

  close(): void {
    this.overlay.classList.remove("modal-overlay--visible");
    this.overlay.setAttribute("aria-hidden", "true");
    this.activeResizeObserver?.disconnect();
    this.activeResizeObserver = null;
    this.panel.innerHTML = "";
    this.panel.className = "modal-panel";
  }

  // ─── Overlay Construction ────────────────────────────────────────────────────

  private buildOverlay(root: HTMLElement): void {
    this.overlay = document.createElement("div");
    this.overlay.className = "modal-overlay";
    this.overlay.setAttribute("role", "dialog");
    this.overlay.setAttribute("aria-modal", "true");
    this.overlay.setAttribute("aria-hidden", "true");

    this.overlay.addEventListener("click", (e) => {
      if (e.target === this.overlay) this.close();
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen()) this.close();
    });

    this.overlay.addEventListener("keydown", (e) => {
      if (e.key !== "Tab" || !this.isOpen()) return;
      const focusable = Array.from(
        this.panel.querySelectorAll<HTMLElement>(
          "button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
        )
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    this.panel = document.createElement("div");
    this.panel.className = "modal-panel";
    this.overlay.appendChild(this.panel);
    root.appendChild(this.overlay);
  }

  private open(): void {
    this.overlay.classList.add("modal-overlay--visible");
    this.overlay.setAttribute("aria-hidden", "false");
    requestAnimationFrame(() => {
      const first = this.panel.querySelector<HTMLElement>(
        "button:not([disabled]), [href], input"
      );
      first?.focus();
    });
  }

  private isOpen(): boolean {
    return this.overlay.classList.contains("modal-overlay--visible");
  }

  // ─── Help Panel ──────────────────────────────────────────────────────────────

  private renderHelp(): void {
    this.panel.innerHTML = "";
    this.panel.setAttribute("aria-labelledby", "modal-title");

    const header = this.buildHeader("How to Play GridCrash");

    const body = document.createElement("div");
    body.className = "modal-body";
    body.innerHTML = `
      <p class="modal-intro">
        GridCrash is competitive Minesweeper. Every flag, every second,
        and every wrong guess affects your final score.
      </p>

      <div class="modal-section">
        <h3 class="modal-section-title">
          <span class="modal-section-icon" aria-hidden="true">🎮</span>
          Controls
        </h3>
        <table class="modal-table" role="table">
          <thead>
            <tr><th>Action</th><th>Desktop</th><th>Mobile</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Reveal cell</td>
              <td><kbd>Click</kbd> or <kbd>Enter</kbd></td>
              <td>Tap</td>
            </tr>
            <tr>
              <td>Place / remove flag</td>
              <td><kbd>Right-click</kbd> or <kbd>Space</kbd></td>
              <td>Long-press 500ms</td>
            </tr>
            <tr>
              <td>Navigate grid</td>
              <td><kbd>←</kbd> <kbd>↑</kbd> <kbd>→</kbd> <kbd>↓</kbd></td>
              <td>–</td>
            </tr>
            <tr>
              <td>New game (same diff)</td>
              <td><kbd>N</kbd></td>
              <td>Tap difficulty</td>
            </tr>
            <tr>
              <td>Restart instantly</td>
              <td><kbd>R</kbd></td>
              <td>Tap New Game</td>
            </tr>
            <tr>
              <td>This help</td>
              <td><kbd>?</kbd></td>
              <td>Tap Help</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="modal-section">
        <h3 class="modal-section-title">
          <span class="modal-section-icon" aria-hidden="true">⚡</span>
          Scoring Formula
        </h3>
        <div class="modal-formula">
          <div class="modal-formula__row modal-formula__row--total">
            <span class="modal-formula__label">Final Score</span>
            <span class="modal-formula__eq">= Base + Win Bonus + Speed Bonus</span>
          </div>
          <div class="modal-formula__row">
            <span class="modal-formula__key">Base</span>
            <span class="modal-formula__val">(Correct Flags × 10) − (Wrong Flags × 5)</span>
          </div>
          <div class="modal-formula__row">
            <span class="modal-formula__key">Win Bonus</span>
            <span class="modal-formula__val">+100 on full board clear</span>
          </div>
          <div class="modal-formula__row">
            <span class="modal-formula__key">Speed Bonus</span>
            <span class="modal-formula__val">+100 / +75 / +50 / +25 / +0 by time tier</span>
          </div>
        </div>
        <table class="modal-table" role="table" style="margin-top: 8px">
          <thead>
            <tr><th>Speed</th><th>Easy</th><th>Medium</th><th>Expert</th></tr>
          </thead>
          <tbody>
            <tr><td>+100</td><td>≤ 30s</td><td>≤ 90s</td><td>≤ 180s</td></tr>
            <tr><td>+75</td><td>≤ 60s</td><td>≤ 180s</td><td>≤ 360s</td></tr>
            <tr><td>+50</td><td>≤ 120s</td><td>≤ 360s</td><td>≤ 600s</td></tr>
            <tr><td>+25</td><td>≤ 300s</td><td>≤ 600s</td><td>≤ 1200s</td></tr>
            <tr><td>+0</td><td>Any</td><td>Any</td><td>Any</td></tr>
          </tbody>
        </table>
      </div>

      <div class="modal-section">
        <h3 class="modal-section-title">
          <span class="modal-section-icon" aria-hidden="true">💣</span>
          Difficulty Tiers
        </h3>
        <table class="modal-table" role="table">
          <thead>
            <tr><th>Tier</th><th>Grid</th><th>Mines</th><th>Density</th></tr>
          </thead>
          <tbody>
            <tr><td>Easy</td><td>9 × 9</td><td>10</td><td>12%</td></tr>
            <tr><td>Medium</td><td>16 × 16</td><td>40</td><td>16%</td></tr>
            <tr><td>Expert</td><td>16 × 30</td><td>99</td><td>21%</td></tr>
          </tbody>
        </table>
      </div>

      <div class="modal-section">
        <h3 class="modal-section-title">
          <span class="modal-section-icon" aria-hidden="true">🔒</span>
          No-Guess Mode
        </h3>
        <p class="modal-text">
          Toggle <strong>No-Guess</strong> in the HUD to generate boards that are
          always logically solvable — no 50/50 coin flips required.
        </p>
      </div>

      <div class="modal-section">
        <h3 class="modal-section-title">
          <span class="modal-section-icon" aria-hidden="true">🛡️</span>
          First-Tap Safety
        </h3>
        <p class="modal-text">
          Your first tap and its 8 neighbors are always mine-free.
          Mines are placed <em>after</em> your first click.
        </p>
      </div>
    `;

    this.panel.appendChild(header);
    this.panel.appendChild(body);
  }

  // ─── Game End Panel ──────────────────────────────────────────────────────────

  private renderGameEnd(
    won:          boolean,
    score:        number,
    timeElapsed:  number,
    difficulty:   DifficultyConfig,
    rank:         number | null,
    entries:      LeaderboardEntry[],
    mode:         "random" | "deterministic",
    correctFlags: number,
    wrongFlags:   number,
    shareText:    string
  ): void {
    this.panel.innerHTML = "";
    this.panel.setAttribute("aria-labelledby", "modal-title");
    this.panel.classList.toggle("modal-panel--win",  won);
    this.panel.classList.toggle("modal-panel--lose", !won);

    const titleText = won ? "🏁 Board Cleared!" : "💥 Mine Detonated";
    const header = this.buildHeader(titleText);

    const body = document.createElement("div");
    body.className = "modal-body";

    // ── Rank banner ──
    if (rank !== null) {
      const rankBanner = document.createElement("div");
      rankBanner.className = "game-end__rank-banner";
      rankBanner.innerHTML = `
        <span class="game-end__rank-emoji">${rankLabel(rank)}</span>
        <div class="game-end__rank-text">
          <span class="game-end__rank-title">New High Score!</span>
          <span class="game-end__rank-sub">${difficulty.label} · Rank ${rank}</span>
        </div>
      `;
      body.appendChild(rankBanner);
    }

    // ── Score total ──
    const scoreSection = document.createElement("div");
    scoreSection.className = "game-end__score-section";
    scoreSection.innerHTML = `
      <div class="game-end__score-total" aria-label="Final score: ${score}">${score}</div>
      <div class="game-end__score-label">points</div>
    `;
    body.appendChild(scoreSection);

    // ── Score breakdown ──
    // Speed bonus always shown — it applies win or lose.
    // Win bonus is withheld on a blow-up.
    const speedBonus = this.getSpeedBonus(timeElapsed, difficulty.speedTiers);

    const breakdown = document.createElement("div");
    breakdown.className = "game-end__breakdown";
    breakdown.innerHTML = `
      <div class="game-end__breakdown-title">Score Breakdown</div>
      <div class="game-end__breakdown-row">
        <span class="game-end__breakdown-key">✅ Correct flags (${correctFlags} × 10)</span>
        <span class="game-end__breakdown-val game-end__breakdown-val--pos">+${correctFlags * 10}</span>
      </div>
      ${wrongFlags > 0 ? `
      <div class="game-end__breakdown-row">
        <span class="game-end__breakdown-key">❌ Wrong flags (${wrongFlags} × 5)</span>
        <span class="game-end__breakdown-val game-end__breakdown-val--neg">−${wrongFlags * 5}</span>
      </div>` : ""}
      ${won ? `
      <div class="game-end__breakdown-row">
        <span class="game-end__breakdown-key">🏁 Win bonus</span>
        <span class="game-end__breakdown-val game-end__breakdown-val--pos">+${difficulty.winBonus}</span>
      </div>` : ""}
      <div class="game-end__breakdown-row">
        <span class="game-end__breakdown-key">⚡ Speed bonus (${formatTime(timeElapsed)})</span>
        <span class="game-end__breakdown-val ${speedBonus > 0 ? "game-end__breakdown-val--pos" : ""}">
          ${speedBonus > 0 ? "+" : ""}${speedBonus}
        </span>
      </div>
      <div class="game-end__breakdown-row game-end__breakdown-row--total">
        <span class="game-end__breakdown-key">Total</span>
        <span class="game-end__breakdown-val">${score}</span>
      </div>
      <div class="game-end__breakdown-meta">
        ${difficulty.label} · ${mode === "deterministic" ? "No-Guess" : "Classic"}
      </div>
    `;
    body.appendChild(breakdown);

    // ── Leaderboard top-5 ──
    if (entries.length > 0) {
      const lbSection = document.createElement("div");
      lbSection.className = "game-end__leaderboard";
      const lbTitle = this.el("h3", "modal-section-title",
        `${difficulty.label} Top ${Math.min(entries.length, 5)}`);
      lbSection.appendChild(lbTitle);
      lbSection.appendChild(this.buildLeaderboardTable(entries.slice(0, 5), score));
      body.appendChild(lbSection);
    }

    // ── Global submission (only if Supabase configured) ──
    if (isGlobalLeaderboardEnabled()) {
      const submitBox = this.buildGlobalSubmitBox(
        score, difficulty.leaderboardKey, mode, timeElapsed
      );
      body.appendChild(submitBox);
    }

    // ── Actions ──
    const actions = document.createElement("div");
    actions.className = "game-end__actions";

    // Share Score button
    const shareBtn = this.actionButton(
      "📤 Share Score",
      "modal-btn modal-btn--ghost",
      () => this.shareScore(shareText)
    );
    actions.appendChild(shareBtn);

    const playAgainBtn = this.actionButton(
      "Play Again",
      "modal-btn modal-btn--primary",
      () => { this.close(); this.callbacks.onNewGame(); }
    );
    actions.appendChild(playAgainBtn);

    body.appendChild(actions);
    this.panel.appendChild(header);
    this.panel.appendChild(body);
  }

  // ─── Share Score ──────────────────────────────────────────────────────────────

  private shareScore(text: string): void {
    if (navigator.share !== undefined) {
      navigator.share({ title: "GridCrash", text }).catch(() => {
        // User cancelled or share failed — fall back to clipboard silently
        this.copyToClipboard(text);
      });
    } else {
      this.copyToClipboard(text);
    }
  }

  private copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.showCopyToast("✅ Score copied to clipboard!");
    }).catch(() => {
      // Last resort — select a temp textarea
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.cssText = "position:fixed;opacity:0;pointer-events:none";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
      this.showCopyToast("✅ Score copied!");
    });
  }

  private showCopyToast(message: string): void {
    const existing = document.querySelector(".copy-toast");
    existing?.remove();

    const toast = document.createElement("div");
    toast.className = "copy-toast";
    toast.setAttribute("role", "status");
    toast.setAttribute("aria-live", "polite");
    toast.textContent = message;
    document.body.appendChild(toast);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => toast.classList.add("copy-toast--visible"));
    });

    setTimeout(() => {
      toast.classList.remove("copy-toast--visible");
      toast.addEventListener("transitionend", () => toast.remove(), { once: true });
    }, 2500);
  }

  // ─── Leaderboard Panel ────────────────────────────────────────────────────────

  private renderLeaderboard(
    activeTierKey: string,
    activeTierLabel: string,
    startOnPicker = false,
    onPickerSelect?: (emoji: string) => void,
    startOnQuests = false
  ): void {
    void activeTierKey; void activeTierLabel; // used conditionally below
    this.panel.innerHTML = "";
    this.panel.classList.add("modal-panel--leaderboard");
    this.panel.setAttribute("aria-labelledby", "modal-title");

    // Header + tab bar share one sticky wrapper so the tabs stay pinned
    // under the title instead of scrolling away with tall tab content
    // (e.g. Quests) — see .lb-sticky-head in main.css.
    const stickyHead = document.createElement("div");
    stickyHead.className = "lb-sticky-head";
    this.panel.appendChild(stickyHead);

    const header = this.buildHeader("Scores");
    stickyHead.appendChild(header);

    // ── Tab bar: Easy / Medium / Expert / 😎 Mascot ──
    const tabBar = document.createElement("div");
    tabBar.className = "lb-tabs";
    tabBar.setAttribute("role", "tablist");
    tabBar.setAttribute("aria-label", "Select leaderboard tier");
    stickyHead.appendChild(tabBar);

    const contentArea = document.createElement("div");
    contentArea.className = "lb-content";

    const renderScoreTab = (tierKey: string, tierLabel: string, isActive: boolean): void => {
      const tab = document.createElement("button");
      tab.className = `lb-tab${isActive ? " lb-tab--active" : ""}`;
      tab.textContent = tierLabel;
      tab.setAttribute("role", "tab");
      tab.setAttribute("aria-selected", isActive ? "true" : "false");
      tab.setAttribute("aria-controls", "lb-panel");
      tab.addEventListener("click", () => {
        setActive(tab);
        this.renderLeaderboardContent(contentArea, tierKey, tierLabel);
      });
      tabBar.appendChild(tab);
    };

    const pickerTab = document.createElement("button");
    pickerTab.className = `lb-tab lb-tab--picker${startOnPicker ? " lb-tab--active" : ""}`;
    pickerTab.textContent = "😎";
    pickerTab.setAttribute("role", "tab");
    pickerTab.setAttribute("aria-selected", startOnPicker ? "true" : "false");
    pickerTab.setAttribute("aria-controls", "lb-panel");

    const setActive = (activeTab: HTMLButtonElement): void => {
      tabBar.querySelectorAll<HTMLButtonElement>(".lb-tab").forEach((t) => {
        t.classList.remove("lb-tab--active");
        t.setAttribute("aria-selected", "false");
      });
      activeTab.classList.add("lb-tab--active");
      activeTab.setAttribute("aria-selected", "true");
    };

    // ── Global tab (only if Supabase configured) ──
    if (isGlobalLeaderboardEnabled()) {
      const globalTab = document.createElement("button");
      globalTab.className = "lb-tab";
      globalTab.textContent = "🌐 Global";
      globalTab.setAttribute("role", "tab");
      globalTab.setAttribute("aria-selected", "false");
      globalTab.setAttribute("aria-controls", "lb-panel");
      globalTab.addEventListener("click", () => {
        setActive(globalTab);
        void this.renderGlobalLeaderboard(contentArea, activeTierKey, "alltime");
      });
      tabBar.appendChild(globalTab);
    }

    // ── Store tab ──
    const storeTab = document.createElement("button");
    storeTab.className = "lb-tab";
    storeTab.textContent = "🛍️ Store";
    storeTab.setAttribute("role", "tab");
    storeTab.setAttribute("aria-selected", "false");
    storeTab.setAttribute("aria-controls", "lb-panel");
    storeTab.addEventListener("click", () => {
      setActive(storeTab);
      void this.renderStoreContent(contentArea);
    });

    // ── Quests tab ──
    const questsTab = document.createElement("button");
    questsTab.className = `lb-tab${startOnQuests ? " lb-tab--active" : ""}`;
    questsTab.textContent = "🎯 Quests";
    questsTab.setAttribute("role", "tab");
    questsTab.setAttribute("aria-selected", startOnQuests ? "true" : "false");
    questsTab.setAttribute("aria-controls", "lb-panel");
    const openQuests = (): void => {
      setActive(questsTab);
      void import("./quests_tab.js").then((m) => {
        m.renderQuestsTab(contentArea, {
          showToast: (msg) => this.showCopyToast(msg),
          onQuestsChanged: () => this.callbacks.onQuestsChanged?.(),
        });
      });
    };
    questsTab.addEventListener("click", openQuests);

    // ── Achievements tab ──
    const achTab = document.createElement("button");
    achTab.className = "lb-tab";
    achTab.textContent = "🏅 Badges";
    achTab.setAttribute("role", "tab");
    achTab.setAttribute("aria-selected", "false");
    achTab.setAttribute("aria-controls", "lb-panel");
    achTab.addEventListener("click", () => {
      setActive(achTab);
      contentArea.innerHTML = "";
      contentArea.id = "lb-panel";
      contentArea.setAttribute("role", "tabpanel");
      const body = document.createElement("div");
      body.className = "modal-body";
      body.appendChild(this.buildAchievementsHTML());
      contentArea.appendChild(body);
    });

    pickerTab.addEventListener("click", () => {
      setActive(pickerTab);
      contentArea.innerHTML = "";
      contentArea.id = "lb-panel";
      contentArea.setAttribute("role", "tabpanel");
      const pickerBody = document.createElement("div");
      pickerBody.className = "modal-body";
      const pickerEl = buildEmojiPickerHTML(onPickerSelect ?? ((_e) => {}));
      pickerBody.appendChild(pickerEl);
      contentArea.appendChild(pickerBody);
    });

    Object.values(DIFFICULTIES).forEach((d) => {
      const isActive = !startOnPicker && !startOnQuests && d.leaderboardKey === activeTierKey;
      renderScoreTab(d.leaderboardKey, d.label, isActive);
    });

    tabBar.appendChild(pickerTab);
    tabBar.appendChild(questsTab);
    tabBar.appendChild(storeTab);
    tabBar.appendChild(achTab);
    this.updateTabOverflow(tabBar);
    tabBar.addEventListener("scroll", () => this.updateTabOverflow(tabBar), { passive: true });
    const tabBarResizeObserver = new ResizeObserver(() => this.updateTabOverflow(tabBar));
    tabBarResizeObserver.observe(tabBar);
    this.activeResizeObserver = tabBarResizeObserver;

    if (startOnPicker) {
      contentArea.innerHTML = "";
      contentArea.id = "lb-panel";
      contentArea.setAttribute("role", "tabpanel");
      const pickerBody = document.createElement("div");
      pickerBody.className = "modal-body";
      const pickerEl = buildEmojiPickerHTML(onPickerSelect ?? ((_e) => {}));
      pickerBody.appendChild(pickerEl);
      contentArea.appendChild(pickerBody);
    } else if (startOnQuests) {
      openQuests();
    } else {
      this.renderLeaderboardContent(contentArea, activeTierKey, activeTierLabel);
    }

    this.panel.appendChild(contentArea);
  }

  private renderLeaderboardContent(
    container: HTMLElement,
    tierKey: string,
    _tierLabel: string
  ): void {
    container.innerHTML = "";
    container.id = "lb-panel";
    container.setAttribute("role", "tabpanel");

    const entries = getTopScores(tierKey);

    const body = document.createElement("div");
    body.className = "modal-body";

    if (entries.length === 0) {
      body.appendChild(
        this.el("p", "modal-empty",
          "No scores yet — play a game to get on the board!")
      );
    } else {
      body.appendChild(this.buildLeaderboardTable(entries, null));
    }

    container.appendChild(body);
  }

  // ─── Store Panel ─────────────────────────────────────────────────────────────

  /**
   * Cosmetics store — rendering lives in store_tab.ts (offline-first,
   * catalog-driven). The modal only wires the tab in.
   */
  private async renderStoreContent(container: HTMLElement): Promise<void> {
    container.innerHTML = "";
    container.id = "lb-panel";
    container.setAttribute("role", "tabpanel");
    container.innerHTML = `<div class="modal-body"><p class="modal-empty">Loading store…</p></div>`;

    const { renderStoreTab } = await import("./store_tab.js");
    await renderStoreTab(container, {
      onCosmeticsChanged: () => this.callbacks.onCosmeticsChanged?.(),
      showToast: (msg) => this.showCopyToast(msg),
    });
  }

  // ─── Daily Challenge Panel ───────────────────────────────────────────────────

  private renderDailyChallenge(): void {
    this.panel.innerHTML = "";
    this.panel.setAttribute("aria-labelledby", "modal-title");

    const header = this.buildHeader("📅 Daily Challenge");
    this.panel.appendChild(header);

    const body = document.createElement("div");
    body.className = "modal-body";

    const progress = getDailyProgress();
    const summary  = getDailySummary();

    // ── Intro blurb ──
    const intro = document.createElement("p");
    intro.className = "modal-intro";
    intro.innerHTML = `
      Every day, all players get the <strong>same board</strong> — generated from
      today's date. One attempt per tier. Post your name to see if you make the
      global leaderboard!
    `;
    body.appendChild(intro);

    // ── Daily summary banner ──
    const banner = document.createElement("div");
    banner.className = "daily-banner";
    banner.innerHTML = `<span class="daily-banner__text">${summary}</span>`;
    body.appendChild(banner);

    // ── Tier cards ──
    const tiers: Array<{ tier: DailyTier; label: string; detail: string }> = [
      { tier: "easy",   label: "Easy",   detail: "9×9 · 10 mines" },
      { tier: "medium", label: "Medium", detail: "16×16 · 40 mines" },
      { tier: "hard",   label: "Expert", detail: "16×30 · 99 mines" },
    ];

    const tierGrid = document.createElement("div");
    tierGrid.className = "daily-tier-grid";

    tiers.forEach(({ tier, label, detail }) => {
      const status = getDailyTierStatus(tier);
      const attempt = progress[tier];

      const card = document.createElement("div");
      card.className = `daily-tier-card daily-tier-card--${status}`;

      const statusEmoji = status === "done-win"  ? "✅"
                        : status === "done-lose" ? "💥"
                        : status === "locked"    ? "🔒"
                        : "🎯";

      const scoreLine = attempt?.completed
        ? `<span class="daily-tier-card__score">${attempt.score} pts · ${formatTime(attempt.timeSecs)}</span>`
        : "";

      card.innerHTML = `
        <span class="daily-tier-card__emoji">${statusEmoji}</span>
        <span class="daily-tier-card__label">${label}</span>
        <span class="daily-tier-card__detail">${detail}</span>
        ${scoreLine}
      `;

      // Only allow playing if not yet attempted
      if (status === "available") {
        const playBtn = document.createElement("button");
        playBtn.className = "daily-tier-card__btn";
        playBtn.textContent = `Play ${label}`;
        playBtn.addEventListener("click", () => {
          this.close();
          this.callbacks.onStartDailyTier(tier);
        });
        card.appendChild(playBtn);
      }

      tierGrid.appendChild(card);
    });

    body.appendChild(tierGrid);

    // ── Submit to global leaderboard (shown only when all 3 tiers complete) ──
    if (progress.allComplete) {
      const submitSection = this.buildDailySubmitSection(progress.totalScore);
      body.appendChild(submitSection);
    }

    // ── Reset note ──
    const resetNote = document.createElement("p");
    resetNote.className = "daily-reset-note";
    const now = new Date();
    const msUntilMidnight = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)
    ).getTime() - now.getTime();
    const hoursLeft = Math.floor(msUntilMidnight / 3600000);
    const minsLeft  = Math.floor((msUntilMidnight % 3600000) / 60000);
    resetNote.textContent = `Resets in ${hoursLeft}h ${minsLeft}m (UTC midnight)`;
    body.appendChild(resetNote);

    this.panel.appendChild(body);
  }

  /** Name entry + submit for the daily global leaderboard. Shown after all 3 tiers complete. */
  private renderDailyComplete(): void {
    this.panel.innerHTML = "";
    this.panel.setAttribute("aria-labelledby", "modal-title");

    const progress = getDailyProgress();

    const header = this.buildHeader("📅 Daily Challenge Complete!");
    this.panel.appendChild(header);

    const body = document.createElement("div");
    body.className = "modal-body";

    // ── Celebratory total ──
    const total = document.createElement("div");
    total.className = "daily-complete__total";
    total.innerHTML = `
      <span class="daily-complete__total-num">${progress.totalScore}</span>
      <span class="daily-complete__total-label">total points across all 3 tiers</span>
    `;
    body.appendChild(total);

    // ── Per-tier breakdown ──
    const tiers: Array<{ tier: DailyTier; label: string }> = [
      { tier: "easy",   label: "Easy"   },
      { tier: "medium", label: "Medium" },
      { tier: "hard",   label: "Expert" },
    ];
    const breakdown = document.createElement("div");
    breakdown.className = "daily-complete__breakdown";
    tiers.forEach(({ tier, label }) => {
      const a = progress[tier];
      const row = document.createElement("div");
      row.className = "daily-complete__row";
      const outcome = a?.won ? "✅" : "💥";
      const score = a?.score ?? 0;
      const time = a ? formatTime(a.timeSecs) : "—";
      row.innerHTML = `
        <span class="daily-complete__row-tier">${outcome} ${label}</span>
        <span class="daily-complete__row-score">${score} pts</span>
        <span class="daily-complete__row-time">${time}</span>
      `;
      breakdown.appendChild(row);
    });
    body.appendChild(breakdown);

    // ── Submit prompt (reuses the same builder as the Daily screen) ──
    body.appendChild(this.buildDailySubmitSection(progress.totalScore));

    this.panel.appendChild(body);
  }

  private buildDailySubmitSection(totalScore: number): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "daily-submit";

    const heading = document.createElement("p");
    heading.className = "daily-submit__heading";
    heading.textContent = `🏆 Post your daily score (${totalScore} pts) to the global leaderboard:`;
    wrap.appendChild(heading);

    const row = document.createElement("div");
    row.className = "global-submit__row";

    const input = document.createElement("input");
    input.className = "global-submit__input";
    input.type = "text";
    input.maxLength = 8;
    input.placeholder = "AAA";
    input.value = getLastUsedName();
    input.setAttribute("aria-label", "Player name (up to 8 letters or numbers)");
    input.addEventListener("input", () => {
      const cleaned = input.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
      if (input.value !== cleaned) input.value = cleaned;
    });

    const btn = document.createElement("button");
    btn.className = "global-submit__btn";
    btn.textContent = "Submit";

    const status = document.createElement("div");
    status.className = "global-submit__status";
    status.setAttribute("role", "status");
    status.setAttribute("aria-live", "polite");

    const doSubmit = async (): Promise<void> => {
      const validation = validateName(input.value);
      if (!validation.valid) {
        status.textContent = validation.error ?? "Invalid name";
        status.className = "global-submit__status global-submit__status--error";
        return;
      }

      btn.disabled = true;
      input.disabled = true;
      status.textContent = "Submitting…";
      status.className = "global-submit__status";

      // Get total time from progress
      const prog = getDailyProgress();
      const totalTime = (prog.easy?.timeSecs ?? 0)
                      + (prog.medium?.timeSecs ?? 0)
                      + (prog.hard?.timeSecs ?? 0);

      const { rank, error } = await submitDailyGlobalScore(
        validation.cleaned,
        totalScore,
        totalTime
      );

      if (!error) {
        setLastUsedName(validation.cleaned);
        status.textContent = rank !== null
          ? `✅ Submitted! Daily rank #${rank}`
          : "✅ Daily score saved!";
        status.className = "global-submit__status global-submit__status--success";
      } else {
        btn.disabled = false;
        input.disabled = false;
        status.textContent = error;
        status.className = "global-submit__status global-submit__status--error";
      }
    };

    btn.addEventListener("click", () => { void doSubmit(); });
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { void doSubmit(); }
    });

    row.appendChild(input);
    row.appendChild(btn);
    wrap.appendChild(row);
    wrap.appendChild(status);
    return wrap;
  }

  // ─── Shared Helpers ──────────────────────────────────────────────────────────

  private buildHeader(titleText: string): HTMLElement {
    const header = document.createElement("div");
    header.className = "modal-header";

    const title = this.el("h2", "modal-title", titleText);
    title.id = "modal-title";
    const closeBtn = this.closeButton();

    header.appendChild(title);
    header.appendChild(closeBtn);
    return header;
  }

  // ─── Achievements Panel ─────────────────────────────────────────────────────

  private buildAchievementsHTML(): HTMLElement {
    const state = getAchievementState();
    const earnedSet = new Set(state.earned);

    const wrap = document.createElement("div");

    // Stats bar
    const stats = document.createElement("div");
    stats.className = "emoji-picker__progress-wrap";
    stats.innerHTML = `
      <div class="emoji-picker__progress-label">
        <span class="emoji-picker__lifetime">🏅 ${earnedSet.size} / ${ACHIEVEMENTS.length} badges earned</span>
        <span class="emoji-picker__next">🚩 ${state.totalFlags} flags · 🔥 ${state.winStreak} win streak</span>
      </div>
      <div class="emoji-picker__bar-track">
        <div class="emoji-picker__bar-fill" style="width:${Math.round(earnedSet.size / ACHIEVEMENTS.length * 100)}%"></div>
      </div>
    `;
    wrap.appendChild(stats);

    // ── Title picker (earned titled achievements) ──
    const titles = getAvailableTitles();
    if (titles.length > 0) {
      const titleRow = document.createElement("div");
      titleRow.className = "title-picker";
      const equipped = getEquippedTitle();

      const heading = this.el("h3", "modal-section-title", "🏷️ Your Title");
      wrap.appendChild(heading);

      const makeTitleBtn = (label: string, value: string): HTMLButtonElement => {
        const btn = document.createElement("button");
        const active = equipped === value;
        btn.className = `modal-btn ${active ? "modal-btn--primary" : "modal-btn--ghost"} title-picker__btn`;
        btn.textContent = label;
        btn.addEventListener("click", () => {
          setEquippedTitle(value);
          this.showCopyToast(value ? `🏷️ Title equipped: ${value}` : "Title removed");
          // Repaint the whole panel so active states refresh.
          const parent = wrap.parentElement;
          if (parent) { parent.innerHTML = ""; parent.appendChild(this.buildAchievementsHTML()); }
        });
        return btn;
      };

      titleRow.appendChild(makeTitleBtn("None", ""));
      for (const t of titles) {
        titleRow.appendChild(makeTitleBtn(`${t.achievement.emoji} ${t.title}`, t.title));
      }
      wrap.appendChild(titleRow);

      const titleHint = this.el(
        "p", "title-picker__hint",
        "Your title appears next to your name on the global leaderboard."
      );
      wrap.appendChild(titleHint);
    }

    // ── Badge grid with showcase toggling ──
    const showcase = getShowcase();
    const showcaseHint = this.el(
      "p", "title-picker__hint",
      `⭐ Tap an earned badge to showcase it (${showcase.length}/3 pinned) — showcased badges lead your sidebar.`
    );
    wrap.appendChild(showcaseHint);

    const grid = document.createElement("div");
    grid.className = "achievement-grid";

    // Showcased first, then the rest in table order.
    const ordered = [
      ...showcase.map((id) => ACHIEVEMENTS.find((a) => a.id === id)).filter((a): a is Achievement => !!a),
      ...ACHIEVEMENTS.filter((a) => !showcase.includes(a.id)),
    ];

    for (const a of ordered) {
      const earned = earnedSet.has(a.id);
      const pinned = showcase.includes(a.id);
      const cell = document.createElement("div");
      cell.className = "achievement-cell" +
        (earned ? " achievement-cell--earned" : " achievement-cell--locked") +
        (pinned ? " achievement-cell--pinned" : "");
      cell.innerHTML = `
        ${pinned ? '<span class="achievement-cell__pin">⭐</span>' : ""}
        <span class="achievement-cell__emoji">${earned ? a.emoji : "🔒"}</span>
        <span class="achievement-cell__name">${a.name}</span>
        <span class="achievement-cell__desc">${a.description}</span>
        ${earned ? '<span class="achievement-cell__earned-badge">✓ Earned</span>' : ""}
      `;
      if (earned) {
        cell.setAttribute("role", "button");
        cell.setAttribute("tabindex", "0");
        cell.setAttribute("aria-pressed", pinned ? "true" : "false");
        const toggle = (): void => {
          const result = toggleShowcase(a.id);
          if (result === null && !pinned) {
            this.showCopyToast("⭐ Showcase is full — unpin a badge first (max 3)");
            return;
          }
          const parent = wrap.parentElement;
          if (parent) { parent.innerHTML = ""; parent.appendChild(this.buildAchievementsHTML()); }
        };
        cell.addEventListener("click", toggle);
        cell.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); }
        });
      }
      grid.appendChild(cell);
    }

    wrap.appendChild(grid);
    return wrap;
  }

  // ─── Global Submit Box ──────────────────────────────────────────────────────

  private buildGlobalSubmitBox(
    score: number,
    tierKey: string,
    mode: "random" | "deterministic",
    timeSecs: number
  ): HTMLElement {
    const box = document.createElement("div");
    box.className = "global-submit";

    const label = document.createElement("label");
    label.className = "global-submit__label";
    label.htmlFor = "global-submit-input";
    label.textContent = "Post to global leaderboard:";

    const inputRow = document.createElement("div");
    inputRow.className = "global-submit__row";

    const input = document.createElement("input");
    input.id = "global-submit-input";
    input.className = "global-submit__input";
    input.type = "text";
    input.maxLength = 8;
    input.placeholder = "AAA";
    input.value = getLastUsedName();
    input.setAttribute("aria-label", "Player initials (up to 5 letters or numbers)");

    // Auto-uppercase + filter to A-Z 0-9
    input.addEventListener("input", () => {
      const cleaned = input.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
      if (input.value !== cleaned) input.value = cleaned;
    });

    const btn = document.createElement("button");
    btn.className = "global-submit__btn";
    btn.textContent = "Submit";

    const status = document.createElement("div");
    status.className = "global-submit__status";
    status.setAttribute("role", "status");
    status.setAttribute("aria-live", "polite");

    const doSubmit = async (): Promise<void> => {
      const validation = validateName(input.value);
      if (!validation.valid) {
        status.textContent = validation.error ?? "Invalid name";
        status.className = "global-submit__status global-submit__status--error";
        return;
      }

      btn.disabled = true;
      input.disabled = true;
      status.textContent = "Submitting…";
      status.className = "global-submit__status";

      const result = await submitScore({
        name: validation.cleaned,
        score,
        tier: tierKey,
        mode,
        timeSecs,
        title: getEquippedTitle(),
      });

      if (result.success) {
        setLastUsedName(validation.cleaned);
        status.textContent = result.rank !== null
          ? `✅ Submitted! Global rank #${result.rank}`
          : "✅ Score saved";
        status.className = "global-submit__status global-submit__status--success";
      } else {
        btn.disabled = false;
        input.disabled = false;
        status.textContent = result.error ?? "Could not submit";
        status.className = "global-submit__status global-submit__status--error";
      }
    };

    btn.addEventListener("click", () => { void doSubmit(); });
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { void doSubmit(); }
    });

    inputRow.appendChild(input);
    inputRow.appendChild(btn);
    box.appendChild(label);
    box.appendChild(inputRow);
    box.appendChild(status);
    return box;
  }

  // ─── Global Leaderboard Sub-Tabs ────────────────────────────────────────────

  private async renderGlobalLeaderboard(
    container: HTMLElement,
    tierKey: string,
    range: TimeRange
  ): Promise<void> {
    container.innerHTML = "";
    const body = document.createElement("div");
    body.className = "modal-body";

    // Time range sub-tabs — Daily Board uses tier="daily", others use the active tier
    const subTabs = document.createElement("div");
    subTabs.className = "lb-subtabs";
    subTabs.setAttribute("role", "tablist");

    // Track whether we're in daily-board mode (separate from time-range "daily")
    const isDailyBoard = tierKey === "daily";

    const makeSub = (lbl: string, r: TimeRange, key?: string): HTMLButtonElement => {
      const t = document.createElement("button");
      const activeKey   = key ?? tierKey;
      const isActive    = key === "daily" ? isDailyBoard : (!isDailyBoard && r === range);
      t.className = "lb-subtab" + (isActive ? " lb-subtab--active" : "");
      t.textContent = lbl;
      t.setAttribute("role", "tab");
      t.addEventListener("click", () => {
        void this.renderGlobalLeaderboard(container, activeKey, r);
      });
      return t;
    };

    subTabs.appendChild(makeSub("Today",       "daily"));
    subTabs.appendChild(makeSub("This Week",   "weekly"));
    subTabs.appendChild(makeSub("All Time",    "alltime"));
    subTabs.appendChild(makeSub("📅 Daily Board", "alltime", "daily"));

    // 🎬 Top Runs — watchable top-100 replays for the active tier
    const runsTab = document.createElement("button");
    runsTab.className = "lb-subtab";
    runsTab.textContent = "🎬 Top Runs";
    runsTab.setAttribute("role", "tab");
    runsTab.addEventListener("click", () => {
      void this.renderTopRuns(container, tierKey);
    });
    subTabs.appendChild(runsTab);
    body.appendChild(subTabs);

    // Loading state
    const loading = document.createElement("p");
    loading.className = "modal-empty";
    loading.textContent = "Loading global scores…";
    body.appendChild(loading);
    container.appendChild(body);

    const scores = await fetchTopScores(tierKey, range, 10);
    loading.remove();

    if (scores.length === 0) {
      const empty = document.createElement("p");
      empty.className = "modal-empty";
      empty.textContent = isDailyBoard
        ? "No daily scores yet — complete all 3 tiers to appear here!"
        : "No scores yet — be the first!";
      body.appendChild(empty);
    } else {
      body.appendChild(this.buildGlobalLeaderboardTable(scores, isDailyBoard));
    }
  }

  /**
   * Top-100 ghost replays for the active tier, watchable in the replay
   * overlay. Requires the Supabase client — offline shows a friendly
   * absence message (house fail-soft style).
   */
  private async renderTopRuns(container: HTMLElement, tierKey: string): Promise<void> {
    container.innerHTML = "";
    const body = document.createElement("div");
    body.className = "modal-body";
    body.innerHTML = `<p class="modal-empty">Loading top runs…</p>`;
    container.appendChild(body);

    const replayMod = await import("../game/replay.js");
    const tier = tierKey.includes("easy") ? "easy"
      : tierKey.includes("medium") ? "medium"
      : tierKey.includes("hard") || tierKey.includes("expert") ? "expert"
      : "daily";
    const runs = await replayMod.fetchTopReplays(tier as import("../game/profile.js").RewardTier, 10);

    body.innerHTML = "";
    const back = document.createElement("button");
    back.className = "modal-btn modal-btn--ghost";
    back.textContent = "← Back to scores";
    back.addEventListener("click", () => {
      void this.renderGlobalLeaderboard(container, tierKey, "alltime");
    });
    body.appendChild(back);

    if (runs.length === 0) {
      const empty = document.createElement("p");
      empty.className = "modal-empty";
      empty.textContent = "Replays unavailable — connect to see the top runs, or be the first to set one!";
      body.appendChild(empty);
      return;
    }

    const list = document.createElement("ol");
    list.className = "replay-list";

    runs.forEach((run, i) => {
      const li = document.createElement("li");
      li.className = "replay-list__row";
      const secs = Math.round(run.duration_ms / 1000);
      li.innerHTML = `
        <span class="leaderboard-table__rank">${rankLabel(i + 1)}</span>
        <span class="replay-list__name">${this.escapeHTML(run.player_name ?? "Anonymous")}</span>
        <span class="replay-list__score">${run.final_score} pts</span>
        <span class="replay-list__time">${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}</span>
      `;

      const watchBtn = document.createElement("button");
      watchBtn.className = "modal-btn modal-btn--primary replay-list__watch";
      const watchable = replayMod.isWatchable(run);
      watchBtn.textContent = watchable ? "▶ Watch" : "Unavailable";
      watchBtn.disabled = !watchable;
      watchBtn.addEventListener("click", async () => {
        watchBtn.disabled = true;
        watchBtn.textContent = "Loading…";
        const events = await replayMod.fetchReplayStream(run.id);
        watchBtn.disabled = false;
        watchBtn.textContent = "▶ Watch";
        if (!events || events.length === 0) {
          this.showCopyToast("❌ Could not load that replay");
          return;
        }
        const { openReplayView } = await import("./replay_view.js");
        openReplayView({
          playerName: run.player_name,
          finalScore: run.final_score,
          difficultyId: run.engine_difficulty!,
          mode: run.mode!,
          seed: Number(run.seed),
          events,
        });
      });
      li.appendChild(watchBtn);

      // Race a ghost of this run on a fresh board (Phase 7).
      if (this.callbacks.onStartGhostRace) {
        const raceBtn = document.createElement("button");
        raceBtn.className = "modal-btn modal-btn--ghost replay-list__watch";
        raceBtn.textContent = watchable ? "🏁 Race" : "Unavailable";
        raceBtn.disabled = !watchable;
        raceBtn.addEventListener("click", async () => {
          raceBtn.disabled = true;
          raceBtn.textContent = "Loading…";
          const events = await replayMod.fetchReplayStream(run.id);
          raceBtn.disabled = false;
          raceBtn.textContent = "🏁 Race";
          if (!events || events.length === 0) {
            this.showCopyToast("❌ Could not load that replay");
            return;
          }
          this.close();
          this.callbacks.onStartGhostRace?.(run, events);
        });
        li.appendChild(raceBtn);
      }

      list.appendChild(li);
    });

    body.appendChild(list);
  }

  private buildGlobalLeaderboardTable(scores: GlobalScore[], hideTier = false): HTMLElement {
    const table = document.createElement("table");
    table.className = "modal-table leaderboard-table";

    const thead = document.createElement("thead");
    thead.innerHTML = hideTier
      ? "<tr><th>#</th><th>Name</th><th>Score</th><th>Time</th><th>Mode</th></tr>"
      : "<tr><th>#</th><th>Name</th><th>Score</th><th>Tier</th><th>Time</th><th>Mode</th></tr>";
    table.appendChild(thead);

    const tbody = document.createElement("tbody");
    scores.forEach((entry, i) => {
      const tr = document.createElement("tr");
      const mins = Math.floor(entry.time_secs / 60);
      const secs = entry.time_secs % 60;
      const timeStr = `${String(mins).padStart(2,"0")}:${String(secs).padStart(2,"0")}`;
      const tierLabel = entry.tier.includes("easy")   ? "Easy"
                      : entry.tier.includes("medium") ? "Medium"
                      : entry.tier.includes("hard") || entry.tier.includes("expert") ? "Expert"
                      : entry.tier;
      const nameCell =
        `${this.escapeHTML(entry.name)}` +
        (entry.title ? ` <span class="leaderboard-table__title">${this.escapeHTML(entry.title)}</span>` : "");
      tr.innerHTML = hideTier ? `
        <td class="leaderboard-table__rank">${rankLabel(i + 1)}</td>
        <td class="leaderboard-table__name">${nameCell}</td>
        <td class="leaderboard-table__score">${entry.score}</td>
        <td class="leaderboard-table__time">${timeStr}</td>
        <td class="leaderboard-table__mode">${entry.mode === "deterministic" ? "No-Guess" : "Classic"}</td>
      ` : `
        <td class="leaderboard-table__rank">${rankLabel(i + 1)}</td>
        <td class="leaderboard-table__name">${nameCell}</td>
        <td class="leaderboard-table__score">${entry.score}</td>
        <td class="leaderboard-table__tier">${tierLabel}</td>
        <td class="leaderboard-table__time">${timeStr}</td>
        <td class="leaderboard-table__mode">${entry.mode === "deterministic" ? "No-Guess" : "Classic"}</td>
      `;
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    return table;
  }

  private escapeHTML(s: string): string {
    const div = document.createElement("div");
    div.textContent = s;
    return div.innerHTML;
  }

  private buildLeaderboardTable(
    entries: LeaderboardEntry[],
    highlightScore: number | null
  ): HTMLElement {
    const table = document.createElement("table");
    table.className = "modal-table leaderboard-table";
    table.setAttribute("role", "table");

    const thead = document.createElement("thead");
    thead.innerHTML =
      "<tr><th>#</th><th>Score</th><th>Mode</th><th>When</th></tr>";
    table.appendChild(thead);

    const tbody = document.createElement("tbody");
    entries.forEach((entry, i) => {
      const tr = document.createElement("tr");
      if (highlightScore !== null && entry.score === highlightScore) {
        tr.classList.add("leaderboard-table__row--highlight");
      }
      tr.innerHTML = `
        <td class="leaderboard-table__rank">${rankLabel(i + 1)}</td>
        <td class="leaderboard-table__score">${entry.score}</td>
        <td class="leaderboard-table__mode">${entry.mode === "deterministic" ? "No-Guess" : "Classic"}</td>
        <td class="leaderboard-table__time">${formatRelativeTime(entry.timestamp)}</td>
      `;
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    return table;
  }

  private getSpeedBonus(
    elapsed: number,
    tiers: Array<[number, number]>
  ): number {
    for (const [maxSeconds, bonus] of tiers) {
      if (elapsed <= maxSeconds) return bonus;
    }
    return 0;
  }

  private el(tag: string, className: string, text?: string): HTMLElement {
    const el = document.createElement(tag);
    el.className = className;
    if (text) el.textContent = text;
    return el;
  }

  /**
   * Toggles the trailing-edge fade on a horizontally-scrollable tab bar.
   * Shown only while there's more to scroll to on the right — hidden once
   * the bar is scrolled to its end, so the cue never lies about content
   * that's already fully visible.
   */
  private updateTabOverflow(tabBar: HTMLElement): void {
    const remaining = tabBar.scrollWidth - tabBar.scrollLeft - tabBar.clientWidth;
    tabBar.classList.toggle("lb-tabs--overflowing", remaining > 1);
  }

  private closeButton(): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.className = "modal-close";
    btn.setAttribute("aria-label", "Close dialog");
    btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
      aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/></svg>`;
    btn.addEventListener("click", () => this.close());
    return btn;
  }

  private actionButton(
    text: string,
    className: string,
    handler: () => void
  ): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.className = className;
    btn.textContent = text;
    btn.addEventListener("click", handler);
    return btn;
  }
}